import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

// Shapes of the /data/shareofsearch/* endpoints (defined in the backend
// OpenAPI spec; no generated types exist on the frontend).
export interface SosLocation {
  locationId: number;
  locationName: string;
  locationParentId: number | null;
  countryCode: string;
  locationTargetType: string;
}

export interface KeywordSearchVolume {
  keyword: string;
  searchVolume: number;
}

export interface SosLocationDatapoint {
  location: SosLocation;
  utcEpoch: number;
  keywords: KeywordSearchVolume[];
}

export interface BrandTrackingConfig {
  brandKeyword: string | null;
  competitorKeywords: string[];
  configured: boolean;
  latestDataDate: string | null;
}

export interface UpdateCompetitorsResult {
  success: boolean;
  keywords: string[];
  message: string;
}

// --- Query Keys ---

const sosCountriesQueryKey = (customer: string) =>
  ['useSosCountriesQuery', customer] as const;

const sosLocationDataQueryKey = (customer: string, locationId: number | null) =>
  ['useSosLocationDataQuery', customer, locationId] as const;

const brandTrackingConfigQueryKey = (customer: string) =>
  ['useBrandTrackingConfigQuery', customer] as const;

// --- Queries ---

export const useSosCountriesQuery = (customer: string) => {
  const queryId = useMemo(() => sosCountriesQueryKey(customer), [customer]);
  return useQuery(
    queryId,
    async () => {
      const response = await request.get<{ data: SosLocation[] }>(
        '/data/shareofsearch/availablecountries',
        { params: { customer } },
      );
      return response.data.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

export const useSosLocationDataQuery = (
  customer: string,
  locationId: number | null,
) => {
  const queryId = useMemo(
    () => sosLocationDataQueryKey(customer, locationId),
    [customer, locationId],
  );
  return useQuery(
    queryId,
    async () => {
      if (locationId === null) return [];
      const response = await request.get<{ data: SosLocationDatapoint[] }>(
        '/data/shareofsearch/location',
        { params: { customer, location_id: String(locationId) } },
      );
      return response.data.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer) && locationId !== null,
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

export const useBrandTrackingConfigQuery = (customer: string) => {
  const queryId = useMemo(
    () => brandTrackingConfigQueryKey(customer),
    [customer],
  );
  return useQuery(
    queryId,
    async () => {
      const response = await request.get<{ data: BrandTrackingConfig }>(
        '/data/brand-tracking/config',
        { params: { customer } },
      );
      return response.data.data ?? null;
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

// --- Mutations ---

export const useUpdateCompetitorsMutation = (customer: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (competitors: string[]) => {
      const response = await request.put<{ data: UpdateCompetitorsResult }>(
        '/data/brand-tracking/competitors',
        { competitors },
        { params: { customer } },
      );
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(brandTrackingConfigQueryKey(customer));
        // New keywords change what the charts should show once data arrives.
        queryClient.invalidateQueries(['useSosLocationDataQuery', customer]);
      },
    },
  );
};
